/**
 * Bootstrap.
 *
 * Flow: the flat page offers an Enter button → a user gesture launches an
 * `immersive-ar` session → passthrough comes up and the in-world UI takes over.
 *
 * The world is created lazily on first Enter rather than at page load. Creating
 * it eagerly allocates a WebGL context and IWSDK's whole system graph for a
 * visitor who may just be reading the page on a desktop.
 */
import {
  ReferenceSpaceType,
  SessionMode,
  World,
  launchXR,
} from '@iwsdk/core';
import { registerUIExtensions, createSceneHost } from '@realitycollective/iwsdk-uiextensions';

import { CLIENT_VERSION, CONFIG, resolveApiBaseUrl } from './config.js';
import { isPassthroughSupported, mountWelcomeCard } from './ui/welcome.js';
import { uiLog } from './ui/uiLog.js';
import { preflightPanels } from './ui/panelPreflight.js';
import { installGuideExperience, PANEL_SOURCE_URLS } from './experience.js';

/** How long to wait for the headset to actually enter the session. */
const SESSION_START_TIMEOUT_MS = 20_000;

async function bootstrap(): Promise<void> {
  const container = document.getElementById('scene-container') as HTMLDivElement | null;
  if (!container) throw new Error('#scene-container missing');

  const card = mountWelcomeCard();
  uiLog.mount();
  uiLog.push(`client ${CLIENT_VERSION}`);

  const apiBaseUrl = resolveApiBaseUrl();
  uiLog.push(`api ${apiBaseUrl}`);

  if (!(await isPassthroughSupported())) {
    card.set('unsupported');
    uiLog.push('immersive-ar not supported on this device');
    return;
  }
  card.set('ready');

  // Deliberately after `ready`: this is a warning, not a gate. A broken panel
  // is worth knowing about here — inside a session there is no console to read
  // it in — but it must never be the reason someone cannot enter.
  void preflightPanels(PANEL_SOURCE_URLS).then((problems) => {
    if (problems.length > 0) {
      card.set('ready', `${problems.length} in-world panel(s) failed to load — see the log.`);
    }
  });

  let world: World | undefined;

  card.onEnter(async () => {
    world ??= await createWorld(container, apiBaseUrl);
    await enterPassthrough(world);
  });
}

async function createWorld(container: HTMLDivElement, apiBaseUrl: string): Promise<World> {
  const world = await World.create(container, {
    render: { near: 0.01, far: 100 },

    xr: {
      sessionMode: SessionMode.ImmersiveAR,
      // Stable across a room-scale session and the space anchors are expressed
      // in; falls back automatically when a runtime declines it.
      referenceSpace: ReferenceSpaceType.LocalFloor,
      // A user gesture starts the session — see the welcome card.
      offer: 'none',
      features: {
        // hit-test is what drives the placement reticle, so the experience is
        // meaningless without it. Everything else degrades gracefully.
        hitTest: { required: true },
        // OPTIONAL, deliberately. Requesting hand tracking as required makes
        // requestSession reject outright on runtimes that decline it; optional
        // lets the session start and Quest supplies whichever input is in use.
        handTracking: true,
        anchors: true,
        planeDetection: true,
        meshDetection: true,
      },
    },

    features: {
      // Live hit-test against passthrough depth. Needs no room scan.
      environmentRaycast: true,
      // Planes and meshes with semantic labels — used to prefer floors and
      // tables. Enrichment only: this DOES need a completed room scan, so
      // placement must never depend on it.
      sceneUnderstanding: true,
      spatialUI: true,
      // UI Extensions ships its own drag handling; IWSDK's grabbing would
      // fight it for the same pointer events.
      grabbing: false,
      locomotion: false,
    },
  });

  const windows = registerUIExtensions(world);
  const host = createSceneHost(world);

  installGuideExperience({ world, host, windows, apiBaseUrl });

  const xr = world.renderer.xr;
  xr.addEventListener('sessionend', () => uiLog.push('session ended'));

  return world;
}

/**
 * `launchXR` is fire-and-forget, so bridge it to a promise the welcome card can
 * await: resolve when the session actually starts, reject if the headset never
 * gets there. Without the timeout a silent `requestSession` failure leaves the
 * button stuck on "Starting…" forever.
 */
function enterPassthrough(world: World): Promise<void> {
  const xr = world.renderer.xr;

  return new Promise<void>((resolve, reject) => {
    const done = (fn: () => void) => {
      clearTimeout(timer);
      xr.removeEventListener('sessionstart', onStart);
      fn();
    };
    const onStart = () => done(resolve);
    const timer = setTimeout(
      () =>
        done(() =>
          reject(new Error('The headset did not start a session. Check passthrough permissions.')),
        ),
      SESSION_START_TIMEOUT_MS,
    );

    xr.addEventListener('sessionstart', onStart);
    launchXR(world, { sessionMode: SessionMode.ImmersiveAR });
  });
}

void bootstrap().catch((error: unknown) => {
  // Nothing else is running yet, so surface this where it can be seen.
  console.error('[bootstrap]', error);
  uiLog.push(`boot failed: ${error instanceof Error ? error.message : String(error)}`);
});

export { CONFIG };
